export type AdminSidebarVisibilityState = {
  /** sectionId -> false (only store hidden sections) */
  sections: Record<string, false>;
  /** `${sectionId}:${itemId}` -> false (only store hidden items) */
  items: Record<string, false>;
};

const STORAGE_KEY = "adminSidebarVisibility:v1";

const emptyState = (): AdminSidebarVisibilityState => ({ sections: {}, items: {} });

export const loadAdminSidebarVisibility = (): AdminSidebarVisibilityState => {
  if (typeof window === "undefined") return emptyState();

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);

    const sections = parsed?.sections && typeof parsed.sections === "object" ? parsed.sections : {};
    const items = parsed?.items && typeof parsed.items === "object" ? parsed.items : {};

    // Normalize to only store `false`
    const normalized: AdminSidebarVisibilityState = emptyState();
    for (const [k, v] of Object.entries(sections)) {
      if (v === false) normalized.sections[k] = false;
    }
    for (const [k, v] of Object.entries(items)) {
      if (v === false) normalized.items[k] = false;
    }
    return normalized;
  } catch {
    return emptyState();
  }
};

export const saveAdminSidebarVisibility = (state: AdminSidebarVisibilityState) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore (private mode / quota)
  }
};

export const clearAdminSidebarVisibility = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Apply saved visibility to a section tree.
 * Important: saved state can only HIDE items; it will never force-show items that are already hidden by role restrictions.
 */
export function applyAdminSidebarVisibility<
  TItem extends { id: string; isVisible: boolean },
  TSection extends { id: string; isVisible: boolean; items: TItem[] },
>(sections: TSection[], state: AdminSidebarVisibilityState): TSection[] {
  return sections.map((section) => {
    const sectionHidden = state.sections[section.id] === false;
    const nextSectionVisible = sectionHidden ? false : section.isVisible;

    const nextItems = section.items.map((item) => {
      const key = `${section.id}:${item.id}`;
      const itemHidden = state.items[key] === false;
      const nextItemVisible = itemHidden ? false : item.isVisible;
      return { ...item, isVisible: nextItemVisible };
    });

    return { ...section, isVisible: nextSectionVisible, items: nextItems };
  });
}

export function deriveAdminSidebarVisibility<
  TItem extends { id: string; isVisible: boolean },
  TSection extends { id: string; isVisible: boolean; items: TItem[] },
>(sections: TSection[]): AdminSidebarVisibilityState {
  const state: AdminSidebarVisibilityState = emptyState();

  for (const section of sections) {
    if (section.isVisible === false) state.sections[section.id] = false;
    for (const item of section.items) {
      if (item.isVisible === false) state.items[`${section.id}:${item.id}`] = false;
    }
  }

  return state;
}
